import React, { Fragment } from 'react';
import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { CardContainer } from '../home/styles';
import NavBar from '../../components/navbar/navbar';
import FooterBar from '../../components/footerBar/footerBar';
import FolderIcon from '@mui/icons-material/Folder';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { folders, verify } from '../../utils/common'; // ← si ya no lo usas, puedes quitarlo después
import { FolderItem } from '../home/types'; // ← idem, si no se usa
import { useLocation, useNavigate } from 'react-router-dom';

import { SupportMaterialService } from '../../components/SupportMaterial/supportMaterialService';
import { SupportMaterialFile, FolderData } from '../../components/SupportMaterial/types';

const Folders: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Estados y helpers DENTRO del componente
  const [listOpen, setListOpen] = React.useState<string>('');
  const [foldersData, setFoldersData] = React.useState<FolderData[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const formatFileSize = (bytes: number): string => {
    if (!bytes) return '';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Autenticación (se mantiene igual)
  React.useEffect(() => {
    (async () => {
      if (!['/', '/login'].includes(location.pathname)) {
        const currentToken = localStorage.getItem('jwt');
        if (currentToken) {
          const isValid = await verify(currentToken);
          if (!isValid) {
            navigate('/');
          }
        } else {
          navigate('/');
        }
      }
    })();
  }, [location.pathname, navigate]);

  // Cargar datos dinámicos
  React.useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await SupportMaterialService.listAllByFolders();
        setFoldersData(data);
      } catch (err: any) {
        setError('No pudimos cargar los materiales de apoyo. Intenta más tarde.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const renderFolders = () => {
    if (loading) {
      return <Typography>Cargando materiales...</Typography>;
    }

    if (error) {
      return <Typography color="error">{error}</Typography>;
    }

    if (foldersData.length === 0) {
      return <Typography>No hay materiales de apoyo disponibles.</Typography>;
    }

    return foldersData.map((folderData) => (
      <Fragment key={folderData.folder}>
        <ListItemButton
          onClick={() => {
            setListOpen((prev) => (prev === folderData.folder ? '' : folderData.folder));
          }}
        >
          <ListItemIcon>
            <FolderIcon color="secondary" />
          </ListItemIcon>
          <ListItemText
            primary={folderData.folder.replaceAll('_', ' ')}
            secondary={`${folderData.files.length} archivos`}
          />
          {listOpen === folderData.folder ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>

        <Collapse in={listOpen === folderData.folder} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {folderData.files.map((file) => (
              <ListItemButton
                key={file.id}
                sx={{ pl: 6 }}
                onClick={() => {
                  if (file.url) {
                    window.open(file.url, '_blank');
                  }
                }}
              >
                <ListItemIcon>
                  <InsertDriveFileIcon color="secondary" />
                </ListItemIcon>
                <ListItemText
                  primary={file.fileName}
                  secondary={
                    <>
                      {file.description && (
                        <>
                          {file.description}
                          <br />
                        </>
                      )}
                      {formatFileSize(file.sizeBytes || 0)}
                    </>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      </Fragment>
    ));
  };

  return (
    <CardContainer theme={theme}>
      <NavBar />
      <Box m={3}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{ color: theme.palette.secondary.main, mb: 3 }}
        >
          Materiales de Apoyo
        </Typography>

        {loading ? (
          <Typography>Cargando...</Typography>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : foldersData.length === 0 ? (
          <Typography>No hay materiales disponibles en este momento.</Typography>
        ) : (
          <List sx={{ bgcolor: 'background.paper', borderRadius: 2, overflow: 'hidden' }}>
            {renderFolders()}
          </List>
        )}
      </Box>
      <FooterBar />
    </CardContainer>
  );
};

export default Folders;